import { Box, Paper, Stack, Typography, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import { CoverPageTemplate } from "../../../../../../proposal/types/cover.types";
import { COVER_TEMPLATES } from "../../../../../../proposal/proposal-preview/templates/coverTemplates";
import { tokens } from "../../../../../../../theme";

interface CoverPagesSectionProps {
  bookingType?: string;
  onTemplateSelect?: (template: CoverPageTemplate | null) => void;
}

const getCoverCategory = (bookingType?: string): string => {
  const map: Record<string, string> = {
    FUNERAL: "funeral",
    Funeral: "funeral",
    Funeral_Home: "funeral",
    Church: "funeral",
    Other: "funeral",
    EVENT: "event",
    CONFERENCE: "conference",
    Conference: "conference",
  };
  return map[bookingType || ""] || "funeral";
};

export default function CoverPagesSection({
  bookingType,
  onTemplateSelect,
}: CoverPagesSectionProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const category = getCoverCategory(bookingType);
  const initialised = useRef(false);

  const templates = COVER_TEMPLATES.filter(
    (t) => t.is_active && t.category.toLowerCase() === category,
  );

  useEffect(() => {
    if (!initialised.current && templates.length > 0) {
      initialised.current = true;
      setSelectedId(templates[0].id);
      onTemplateSelect?.(templates[0]);
    }
  }, [templates, onTemplateSelect]);

  const handleSelect = (template: CoverPageTemplate) => {
    setSelectedId(template.id);
    onTemplateSelect?.(template);
  };

  return (
    <Paper sx={{ borderRadius: "14px", p: 2.5, boxShadow: 0, mb: 2 }}>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "15px",
          color: colors.primary[100],
          mb: 2,
        }}
      >
        Cover Pages
      </Typography>

      {templates.length === 0 ? (
        <Typography
          sx={{
            fontSize: "12px",
            color: colors.primary[700],
            textAlign: "center",
            py: 2,
          }}
        >
          No cover templates available for this booking type.
        </Typography>
      ) : (
        <Stack
          direction="row"
          spacing={1.5}
          sx={{
            overflowX: "auto",
            pb: 1,
            flexWrap: "nowrap",
            "&::-webkit-scrollbar": { height: 4 },
            "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "action.hover",
              borderRadius: 4,
            },
          }}
        >
          {templates.map((template) => (
            <Box
              key={template.id}
              onClick={() => handleSelect(template)}
              sx={{
                flex: "0 0 calc(25% - 9px)",
                minWidth: 120,
                cursor: "pointer",
                position: "relative",
              }}
            >
              {/* Image card */}
              <Box
                sx={{
                  aspectRatio: "3/4",
                  borderRadius: "10px",
                  overflow: "hidden",
                  border:
                    selectedId === template.id
                      ? `2px solid ${colors.primary[400]}`
                      : "2px solid transparent",
                  transition: "border-color 0.15s",
                  position: "relative",
                  "&:hover": { borderColor: colors.primary[500] },
                }}
              >
                <img
                  src={template.background_image}
                  alt={template.name}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                />

                {/* Selected checkmark */}
                {selectedId === template.id && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 6,
                      right: 6,
                      bgcolor: "background.paper",
                      borderRadius: "50%",
                      width: 22,
                      height: 22,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CheckCircleRounded
                      sx={{ fontSize: 22, color: colors.primary[400] }}
                    />
                  </Box>
                )}
              </Box>

              {/* Template name */}
              <Typography
                noWrap
                sx={{
                  fontSize: "11px",
                  fontWeight: selectedId === template.id ? 700 : 500,
                  color:
                    selectedId === template.id
                      ? colors.primary[400]
                      : colors.primary[100],
                  mt: 0.8,
                  textAlign: "center",
                  px: 0.5,
                }}
              >
                {template.name}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}
    </Paper>
  );
}
